'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Dialog } from '../../../components/dialog';
import { DOCUMENTATION_FILE_ACCEPT } from './applicationDocumentation.constants';
import type { DocumentationFileCategory } from './applicationDocumentation.types';
import { ApplicationDocumentationFileUploadArea } from './ApplicationDocumentationFileUploadArea';

type FilesDialogCopyKeys = {
  title:
    | 'addFilesDialog.title'
    | 'addApiDocumentationFilesDialog.title'
    | 'addTestingReportsFilesDialog.title';
  description:
    | 'addFilesDialog.description'
    | 'addApiDocumentationFilesDialog.description'
    | 'addTestingReportsFilesDialog.description';
};

const FILES_DIALOG_COPY_KEYS: Record<
  DocumentationFileCategory,
  FilesDialogCopyKeys
> = {
  application: {
    title: 'addFilesDialog.title',
    description: 'addFilesDialog.description',
  },
  api: {
    title: 'addApiDocumentationFilesDialog.title',
    description: 'addApiDocumentationFilesDialog.description',
  },
  'testing-reports': {
    title: 'addTestingReportsFilesDialog.title',
    description: 'addTestingReportsFilesDialog.description',
  },
};

type ApplicationDocumentationFilesDialogProps = {
  category: DocumentationFileCategory | null;
  files: File[];
  fileUploadKey: number;
  isConfirming: boolean;
  onDismiss: () => void;
  onConfirm: () => void | Promise<void>;
  onFilesAccept: (details: { files: File[] }) => void;
  onRemoveFile: ({ fileIndex }: { fileIndex: number }) => void;
};

export const ApplicationDocumentationFilesDialog = ({
  category,
  files,
  fileUploadKey,
  isConfirming,
  onDismiss,
  onConfirm,
  onFilesAccept,
  onRemoveFile,
}: ApplicationDocumentationFilesDialogProps) => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const tCommon = useTranslations('designSystem.common');
  const copyKeys = FILES_DIALOG_COPY_KEYS[category ?? 'application'];
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isConfirming) {
        onDismiss();
      }
    },
    [isConfirming, onDismiss],
  );

  return (
    <Dialog
      open={category !== null}
      onOpenChange={handleOpenChange}
      size="sm"
      title={t(copyKeys.title)}
      description={t(copyKeys.description)}
      secondaryAction={{
        label: tCommon('cancel'),
        onClick: onDismiss,
        disabled: isConfirming,
      }}
      primaryAction={{
        label: t('addFilesDialog.confirm'),
        onClick: onConfirm,
        disabled: files.length === 0,
        loading: isConfirming,
      }}
    >
      <ApplicationDocumentationFileUploadArea
        accept={DOCUMENTATION_FILE_ACCEPT[category ?? 'application']}
        files={files}
        fileUploadKey={fileUploadKey}
        fileUploadRootKeySuffix={`-${category ?? ''}`}
        onFileAccept={onFilesAccept}
        onRemoveFile={onRemoveFile}
        deleteAriaLabel={tCommon('delete')}
        disabled={isConfirming}
        scrollableFileList
      />
    </Dialog>
  );
};
